import React, { useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ROUTES } from "../../constants";
import { AppLogoIcon } from "../../components/Icons";
import s from "./LandingPage.module.css";

const CONTACT_EMAIL = "customersupport@orivodentai.com";

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  // Load Instrument Serif font (not in the main app bundle)
  useEffect(() => {
    const id = "landing-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href =
        "https://fonts.googleapis.com/css2?family=Instrument+Serif:ital@0;1&display=swap";
      document.head.appendChild(link);
    }
  }, []);

  return (
    <div className={s.page}>
      {/* ── NAV ── */}
      <nav className={s.nav}>
        <button className={s.navLogo} onClick={() => navigate(ROUTES.LANDING)}>
          <AppLogoIcon width="148px" height="32px" />
        </button>

        <div className={s.navLinks}>
          <button className={s.btnPrimary} onClick={() => navigate(ROUTES.LOGIN)}>
            Log In
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M2.5 6h7m0 0L7 3.5M9.5 6 7 8.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className={s.hero}>
        <div className={s.heroBg} />

        <svg className={s.heroOrb} viewBox="0 0 520 520" fill="none">
          <circle cx="260" cy="260" r="259" stroke="#1B4D6E" strokeWidth="1.5" />
          <circle cx="260" cy="260" r="200" stroke="#1B4D6E" strokeWidth="1" />
          <circle cx="260" cy="260" r="140" stroke="#1B4D6E" strokeWidth="1" />
          <circle cx="260" cy="260" r="80" fill="#2C6E8A" opacity=".15" />
          <path
            d="M260 120C220 120 180 148 180 185c0 20 6 36 15 51 10 16 12 34 14.5 56 1.5 12.5 6 17 13 17s10.5-6 13-20c2-11.5 5.5-22.5 10.5-22.5S253 278 255 289.5c2.5 14 7 20 14 20s11.5-4.5 13-17c2.5-22 4.5-40 14.5-56 9-15 15-31 15-51 0-37-40-65-51.5-65.5z"
            fill="#3D8EAD"
            opacity=".4"
          />
        </svg>

        <svg className={s.heroOrbLeft} viewBox="0 0 360 360" fill="none">
          <circle cx="180" cy="180" r="179" stroke="#1B4D6E" strokeWidth="1" />
          <circle cx="180" cy="180" r="120" stroke="#1B4D6E" strokeWidth="0.75" />
          <circle cx="180" cy="180" r="60" fill="#2C6E8A" opacity=".2" />
        </svg>

        <div className={s.heroContent}>
          <div className={`${s.heroBadge} ${s.fadeIn}`}>
            <span className={s.heroBadgeDot} />
            Secure · HIPAA-conscious · Invite-only platform
          </div>

          <h1 className={`${s.h1} ${s.fadeIn}`}>
            OrivoDent <em>AI</em>
          </h1>

          <p className={`${s.heroSub} ${s.fadeIn}`}>
            Secure AI-powered dental workflow platform for clinics and providers
          </p>

          <p className={`${s.heroDesc} ${s.fadeIn}`}>
            OrivoDent AI helps dental teams manage treatment planning, session documentation,
            and patient communication in one secure system.
          </p>

          <div className={`${s.heroCta} ${s.fadeIn}`}>
            <button className={s.btnPrimaryLg} onClick={() => navigate(ROUTES.LOGIN)}>
              Log In
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                <path d="M3 7h8m0 0L8 4m3 3-3 3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </button>
            <a href={`mailto:${CONTACT_EMAIL}`} className={s.btnGhostLg}>
              <svg width="15" height="15" viewBox="0 0 15 15" fill="none">
                <path d="M1.5 3.5h12v9h-12z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                <path d="m1.5 3.5 6 5 6-5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Contact Access Team
            </a>
          </div>
        </div>

        {/* Mini UI Mock */}
        <div className={`${s.heroMock} ${s.fadeIn}`}>
          <div className={s.mockWindow}>
            <div className={s.mockTopbar}>
              <div className={s.mockDot} />
              <div className={s.mockDot} />
              <div className={s.mockDot} />
              <div className={s.mockUrlBar}>app.orivodent.com/dashboard</div>
            </div>
            <div className={s.mockBody}>
              <div className={s.mockSidebar}>
                <div className={s.mockLogo}>
                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                    <path d="M11 2C8 2 5 4.5 5 8c0 2 .5 3.5 1.5 5 .8 1.2 1 2.8 1.2 4.5.2 1.2.7 1.5 1.3 1.5s1-.5 1.2-1.7c.2-1.1.5-2.2 1-2.2s.8 1.1 1 2.2c.2 1.2.7 1.7 1.3 1.7s1.1-.3 1.3-1.5c.2-1.7.4-3.3 1.2-4.5C17 11.5 17.5 10 17.5 8c0-3.5-3-6-6.5-6z" fill="#29ABD4" />
                  </svg>
                  <span className={s.mockLogoText}>OrivoDent AI</span>
                </div>
                <div className={s.mockUser}>
                  <div className={s.mockAvatar}>JS</div>
                  <div className={s.mockUsername}>Josh Smith</div>
                </div>
                <div className={s.mockNavItemActive}>
                  <svg className={s.mockNavIcon} viewBox="0 0 14 14" fill="none">
                    <rect x="1" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="8" y="1" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="1" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
                    <rect x="8" y="8" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.5" />
                  </svg>
                  Dashboard
                </div>
                <div className={s.mockNavItem}>
                  <svg className={s.mockNavIcon} viewBox="0 0 14 14" fill="none">
                    <rect x="1" y="2" width="12" height="10" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M4 5h6M4 8h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Sessions
                </div>
                <div className={s.mockNavItem}>
                  <svg className={s.mockNavIcon} viewBox="0 0 14 14" fill="none">
                    <circle cx="7" cy="5" r="3" stroke="currentColor" strokeWidth="1.5" />
                    <path d="M1.5 13c0-2.5 2.5-4.5 5.5-4.5s5.5 2 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Patients
                </div>
              </div>
              <div className={s.mockContent}>
                <div className={s.mockHeader}>
                  <div className={s.mockTitle}>Hi, Wael!</div>
                  <div className={s.mockStartBtn}>Start Session →</div>
                </div>
                <div className={s.mockSearch}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.5" />
                    <path d="m9 9 1.5 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  Search patients or treatment plans…
                </div>
                <div className={s.mockTabs}>
                  <div className={s.mockTabActive}>AI Plans Awaiting Review</div>
                  <div className={s.mockTab}>Recently Edited Plans</div>
                </div>
                <div className={s.mockTableHead}>
                  <div>Patient</div>
                  <div>Treatment Plan</div>
                  <div>Date</div>
                  <div>Action</div>
                </div>
                {[
                  { name: "Mark Peterson", plan: "Crown Replacement", date: "12/02/2025" },
                  { name: "Ryan Kenter", plan: "Full Upper Restoration", date: "11/02/2025" },
                  { name: "Kianna Lubin", plan: "Crown Replacement", date: "08/03/2025" },
                  { name: "Haylie Siphron", plan: "Full Upper Restoration", date: "04/04/2025" },
                ].map((row) => (
                  <div key={row.name} className={s.mockRow}>
                    <div className={s.mockPatient}>{row.name}</div>
                    <div className={s.mockPlan}>{row.plan}</div>
                    <div className={s.mockDate}>{row.date}</div>
                    <div className={s.mockAction}>View</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 2: WHAT IT DOES ── */}
      <section className={s.sectionWhite} id="what">
        <div className={s.container}>
          <span className={s.sectionLabel}>Platform capabilities</span>
          <h2 className={s.h2}>What OrivoDent AI does</h2>
          <p className={s.sectionIntro}>
            A single, connected system for the full clinical workflow — from first session to signed treatment plan.
          </p>

          <div className={s.featuresLayout}>
            <div>
              <ul className={s.featureList}>
                {[
                  {
                    title: "Session recording & AI transcription",
                    desc: "Audio capture of treatment discussions with automatic AI-generated transcripts, ready for review.",
                    icon: <path d="M8.5 2a6.5 6.5 0 1 0 0 13A6.5 6.5 0 0 0 8.5 2z" stroke="#1B4D6E" strokeWidth="1.5"/>,
                  },
                  {
                    title: "Structured treatment plan generation",
                    desc: "AI drafts treatment plans from session data, with procedure codes and pricing matched automatically.",
                    icon: <><rect x="2" y="3" width="13" height="11" rx="1.5" stroke="#1B4D6E" strokeWidth="1.5"/><path d="M5 7h7M5 10h5" stroke="#1B4D6E" strokeWidth="1.5" strokeLinecap="round"/></>,
                  },
                  {
                    title: "Provider review & approval",
                    desc: "Clinicians review AI-generated plans, make adjustments, and approve before any patient-facing sharing.",
                    icon: <path d="M3.5 9.5 6 12l7-7" stroke="#1B4D6E" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>,
                  },
                  {
                    title: "Secure patient access to plans",
                    desc: "Approved treatment plans are shared directly with patients through a secure, access-controlled link.",
                    icon: <><circle cx="8.5" cy="6" r="3" stroke="#1B4D6E" strokeWidth="1.5"/><path d="M2.5 14.5c0-3 2.7-5.5 6-5.5s6 2.5 6 5.5" stroke="#1B4D6E" strokeWidth="1.5" strokeLinecap="round"/></>,
                  },
                  {
                    title: "Centralized case & workflow management",
                    desc: "All patients, sessions, and treatment plans organized in one dashboard, with role-based access control.",
                    icon: <><rect x="2" y="2" width="13" height="13" rx="1.5" stroke="#1B4D6E" strokeWidth="1.5"/><path d="M5 8.5h7M8.5 5v7" stroke="#1B4D6E" strokeWidth="1.5" strokeLinecap="round"/></>,
                  },
                  {
                    title: "Clinic system integration",
                    desc: "Secure access to patient and case data from the connected clinic management system.",
                    icon: <><path d="M8.5 2v2M8.5 13v2M2 8.5h2M13 8.5h2" stroke="#1B4D6E" strokeWidth="1.5" strokeLinecap="round"/><circle cx="8.5" cy="8.5" r="3.5" stroke="#1B4D6E" strokeWidth="1.5"/></>,
                  },
                ].map((f) => (
                  <li key={f.title} className={s.featureItem}>
                    <div className={s.featureIcon}>
                      <svg viewBox="0 0 17 17" fill="none">{f.icon}</svg>
                    </div>
                    <div>
                      <div className={s.featureTitle}>{f.title}</div>
                      <div className={s.featureDesc}>{f.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            <div className={s.featureRight}>
              <div className={s.featureCard}>
                <div className={s.featureCardLabel}>Platform at a glance</div>
                <div className={s.featureStatRow}>
                  {[
                    { num: "AI", label: "Transcription" },
                    { num: "Auto", label: "Plan generation" },
                    { num: "Role", label: "Based access" },
                  ].map((st) => (
                    <div key={st.label} className={s.featureStat}>
                      <div className={s.featureStatNum}>{st.num}</div>
                      <div className={s.featureStatLabel}>{st.label}</div>
                    </div>
                  ))}
                </div>
                <div className={s.featureSteps}>
                  {[
                    { n: 1, text: "Record treatment session audio", ai: true },
                    { n: 2, text: "AI generates transcript & treatment plan", ai: true },
                    { n: 3, text: "Provider reviews and approves", ai: false },
                    { n: 4, text: "Plan shared securely with patient", ai: false },
                  ].map((step) => (
                    <div key={step.n} className={s.featureStep}>
                      <div className={s.featureStepNum}>{step.n}</div>
                      <div className={s.featureStepText}>{step.text}</div>
                      {step.ai && <div className={s.featureStepBadge}>AI</div>}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 3: WHO ── */}
      <section className={s.sectionBg} id="who">
        <div className={s.container}>
          <span className={s.sectionLabel}>Intended users</span>
          <h2 className={s.h2}>Who it is for</h2>
          <p className={s.sectionIntroCentered}>
            OrivoDent AI is designed for dental clinics, providers, and authorized team members
            who need a secure and efficient way to manage treatment planning and patient workflows.
          </p>

          <div className={s.whoCards}>
            {[
              {
                title: "Dental Clinics",
                desc: "Practices of any size looking to streamline clinical documentation and treatment plan management across their patient base.",
                icon: <><rect x="3" y="3" width="20" height="20" rx="3" stroke="#1B4D6E" strokeWidth="1.75"/><path d="M9 13h8M13 9v8" stroke="#1B4D6E" strokeWidth="1.75" strokeLinecap="round"/></>,
              },
              {
                title: "Providers & Dentists",
                desc: "Licensed dental professionals responsible for reviewing AI-generated treatment plans and authorizing patient-facing output.",
                icon: <><circle cx="13" cy="9" r="4.5" stroke="#1B4D6E" strokeWidth="1.75"/><path d="M4.5 22c0-4 3.8-7 8.5-7s8.5 3 8.5 7" stroke="#1B4D6E" strokeWidth="1.75" strokeLinecap="round"/></>,
              },
              {
                title: "Authorized Team Members",
                desc: "Clinical coordinators and staff granted access by clinic administrators to support session management and patient workflows.",
                icon: <><circle cx="9" cy="9" r="3.5" stroke="#1B4D6E" strokeWidth="1.75"/><circle cx="18" cy="9" r="3.5" stroke="#1B4D6E" strokeWidth="1.75"/><path d="M2 22c0-3 3-5.5 7-5.5M24 22c0-3-3-5.5-7-5.5M13 22c0-3.5 2.5-6 5.5-6" stroke="#1B4D6E" strokeWidth="1.75" strokeLinecap="round"/></>,
              },
            ].map((c) => (
              <div key={c.title} className={s.whoCard}>
                <div className={s.whoIcon}>
                  <svg viewBox="0 0 26 26" fill="none">{c.icon}</svg>
                </div>
                <div className={s.whoCardTitle}>{c.title}</div>
                <div className={s.whoCardDesc}>{c.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SECTION 4: SECURITY ── */}
      <section className={s.sectionWhite} id="security">
        <div className={s.container}>
          <span className={s.sectionLabel}>Access &amp; security</span>
          <h2 className={s.h2}>Access and security</h2>
          <p className={s.sectionIntro}>
            How access is managed and why public registration is not available.
          </p>

          <div className={s.securityBox}>
            <div className={s.securityShield}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L4 6v6c0 5 3.5 9.5 8 11 4.5-1.5 8-6 8-11V6L12 2z" stroke="white" strokeWidth="1.75" strokeLinejoin="round" />
                <path d="M9 12l2 2 4-4" stroke="white" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div>
              <h3 className={s.securityH3}>Private, invite-only platform</h3>
              <p className={s.securityP}>
                OrivoDent AI is a private platform. Because the system is used internally by clinics
                and may process sensitive patient information, public self-registration is not available.
              </p>
              <p className={s.securityP}>
                Access is granted only through a secure invitation link issued by the clinic owner or
                an authorized administrator. Each invitation is scoped to a specific clinic and user role.
              </p>
              <div className={s.securityPills}>
                {["Invite-only access", "Role-based permissions", "Two-factor authentication", "Encrypted data handling"].map((pill) => (
                  <div key={pill} className={s.securityPill}>
                    <svg viewBox="0 0 12 12" fill="none">
                      <circle cx="6" cy="6" r="5" stroke="currentColor" strokeWidth="1.2" />
                      <path d="M4 6l1.5 1.5L8 4" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    {pill}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SECTION 5: SMS ── */}
      <section className={s.section} id="sms">
        <div className={s.container}>
          <span className={s.sectionLabel}>SMS use policy</span>
          <h2 className={s.h2}>SMS use</h2>

          <div className={s.smsCard}>
            <div className={s.smsIcon}>
              <svg viewBox="0 0 24 24" fill="none">
                <path d="M4 4h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H6l-4 3V6a2 2 0 0 1 2-2z" stroke="#1B4D6E" strokeWidth="1.75" strokeLinejoin="round" />
                <path d="M8 10h.01M12 10h.01M16 10h.01" stroke="#1B4D6E" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </div>
            <div>
              <h3 className={s.smsH3}>Authentication and security only</h3>
              <p className={s.smsP}>
                OrivoDent AI uses SMS only for authentication and account security purposes,
                including two-factor authentication (2FA) and verification of authorized users.
                SMS messages are never used for marketing, promotional content, or patient-facing
                communications. Message frequency depends on account activity. Standard messaging
                rates may apply.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer className={s.footer}>
        <div className={s.footerInner}>
          <button className={s.footerLogo} onClick={() => navigate(ROUTES.LANDING)}>
            <AppLogoIcon width="148px" height="32px" className={s.footerLogoIcon} />
          </button>

          <ul className={s.footerLinks}>
            <li><span className={s.footerDivider}>·</span></li>
            <li>
              <Link className={s.footerLink} to={ROUTES.TERMS}>Terms of Use</Link>
            </li>
            <li><span className={s.footerDivider}>·</span></li>
            <li>
              <Link className={s.footerLink} to={ROUTES.PRIVACY}>Privacy Policy</Link>
            </li>
            <li><span className={s.footerDivider}>·</span></li>
            <li>
              <Link className={s.footerLink} to={ROUTES.SMS_POLICY}>SMS Messaging Policy</Link>
            </li>
          </ul>

          <div className={s.footerEmail}>
            Questions?{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>
          </div>
        </div>

        <div className={s.footerBottom}>
          <span>© 2026 OrivoDent AI. All rights reserved.</span>
          <span>Private platform — not for public registration</span>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
